<!-- =========================================================================================
	File Name: App.vue
	Description: Main vue file - APP
	----------------------------------------------------------------------------------------
	Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
	Author: Pixinvent
	Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
	<div id="app" :class="vueAppClasses">
		<router-view @setAppClasses="setAppClasses" />
    <portal-target name="app-portal-target"></portal-target>
	</div>
</template>

<script>
import themeConfig from '@/../themeConfig';

export default {
  data() {
    return {
      vueAppClasses: [],
      modalObserver: null,
      isScrolling: false,
      scrollTimeout: null,
      viewportOrientation: null,
      viewportBaseHeight: null,
      visualViewport: null,
      visualViewportResizeRaf: null,
      isChromeIOSViewportFixEnabled: false,
    };
  },
  watch: {
    '$store.state.theme': function (val) {
      this.toggleClassInBody(val);
    },
    '$vs.rtl': function (val) {
      document.documentElement.setAttribute('dir', val ? 'rtl' : 'ltr');
    },
  },
  mounted() {
    this.toggleClassInBody(themeConfig.theme);
    this.$store.commit('UPDATE_WINDOW_WIDTH', window.innerWidth);
    this.isChromeIOSViewportFixEnabled = this.isChromeOniOSBrowser();

    if (this.isChromeIOSViewportFixEnabled) {
      this.initializeViewportHeight();

      if (typeof window !== 'undefined' && window.visualViewport) {
        this.visualViewport = window.visualViewport;
        this.visualViewport.addEventListener('resize', this.handleVisualViewportResize);
      }
    }

    this.initModalObserver();

    // Removed global scroll listener that was auto-closing dropdowns on scroll
    // This was causing iOS elastic scrolling issues (bounce-to-top behavior)
    // Users can close dropdowns by clicking elsewhere or opening another dropdown
  },
  async created() {
    const dir = this.$vs.rtl ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);

    window.addEventListener('resize', this.handleWindowResize);
    window.addEventListener('scroll', this.handleScroll);
    // Use capture phase to handle clicks before they bubble up
    window.addEventListener('click', this.handleDropdownItemClick, true);
  },
  beforeDestroy() {
    if (this.modalObserver) {
      this.modalObserver.disconnect();
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.handleWindowResize);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('click', this.handleDropdownItemClick, true);

    if (this.visualViewport) {
      this.visualViewport.removeEventListener('resize', this.handleVisualViewportResize);
      this.visualViewport = null;
    }

    if (this.visualViewportResizeRaf) {
      cancelAnimationFrame(this.visualViewportResizeRaf);
      this.visualViewportResizeRaf = null;
    }
  },
  methods: {
    getViewportOrientation() {
      return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
    },
    isChromeOniOSBrowser() {
      if (typeof navigator === 'undefined') return false;

      const ua = navigator.userAgent || '';
      const isIPhone = /iPhone|iPod/.test(ua) || ['iPhone', 'iPod'].includes(navigator.platform);
      const isPseudoIPad = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
      const isChromeLike = /CriOS/.test(ua);
      const isEdge = /Edg|EdgiOS|EdgA/.test(ua);
      const isOpera = /OPR|OPiOS/.test(ua);
      const isBrave = typeof navigator.brave !== 'undefined';
      const isSamsung = /SamsungBrowser/.test(ua);

      return isIPhone && !isPseudoIPad && isChromeLike && !isEdge && !isOpera && !isBrave && !isSamsung;
    },
    setViewportHeightVariable(height) {
      if (typeof document === 'undefined') return;
      const hasWindow = typeof window !== 'undefined';
      const targetHeight = typeof height === 'number'
        ? height
        : (hasWindow ? window.innerHeight : 0);

      if (!targetHeight) return;

      document.documentElement.style.setProperty('--vh', `${targetHeight * 0.01}px`);
    },
    initializeViewportHeight() {
      if (typeof window === 'undefined') return;

      this.viewportOrientation = this.getViewportOrientation();
      this.viewportBaseHeight = window.innerHeight;
      this.setViewportHeightVariable(this.viewportBaseHeight);
    },
    updateViewportHeight() {
      if (typeof window === 'undefined') return;

      const currentOrientation = this.getViewportOrientation();
      const currentHeight = window.innerHeight;

      if (!this.viewportOrientation || !this.viewportBaseHeight) {
        this.viewportOrientation = currentOrientation;
        this.viewportBaseHeight = currentHeight;
        this.setViewportHeightVariable(currentHeight);
        return;
      }

      if (currentOrientation !== this.viewportOrientation) {
        this.viewportOrientation = currentOrientation;
        this.viewportBaseHeight = currentHeight;
        this.setViewportHeightVariable(currentHeight);
        return;
      }

      if (currentHeight > this.viewportBaseHeight) {
        this.viewportBaseHeight = currentHeight;
        this.setViewportHeightVariable(currentHeight);
      }
    },
    initModalObserver() {
      const observer = new MutationObserver(() => {
        this.toggleBodyScroll();
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'], // solo si tus modales cambian estilos o clases
      });

      this.modalObserver = observer;

      // Primera comprobación por si ya hay modales abiertos
      this.toggleBodyScroll();
    },

    toggleBodyScroll() {
      const modals = document.querySelectorAll('.vs-component.con-vs-popup');
      const hasVisibleModal = Array.from(modals).some((modal) => getComputedStyle(modal).display !== 'none');
      document.body.classList.toggle('modal-open', hasVisibleModal);
    },

    toggleClassInBody(className) {
      if (className == 'dark') {
        if (document.body.className.match('theme-semi-dark')) document.body.classList.remove('theme-semi-dark');
        document.body.classList.add('theme-dark');
      } else if (className == 'semi-dark') {
        if (document.body.className.match('theme-dark')) document.body.classList.remove('theme-dark');
        document.body.classList.add('theme-semi-dark');
      } else {
        if (document.body.className.match('theme-dark')) document.body.classList.remove('theme-dark');
        if (document.body.className.match('theme-semi-dark')) document.body.classList.remove('theme-semi-dark');
      }
    },
    setAppClasses(classesStr) {
      this.vueAppClasses.push(classesStr);
    },
    handleWindowResize() {
      this.$store.commit('UPDATE_WINDOW_WIDTH', window.innerWidth);
      if (this.isChromeIOSViewportFixEnabled) {
        this.updateViewportHeight();
      }
    },
    handleVisualViewportResize() {
      if (typeof window === 'undefined') return;

      if (!this.isChromeIOSViewportFixEnabled) return;

      const currentScrollY = window.scrollY || window.pageYOffset || 0;
      const currentScrollX = window.scrollX || window.pageXOffset || 0;
      const shouldRestoreScroll = currentScrollY > 0;

      this.updateViewportHeight();

      if (!shouldRestoreScroll) return;

      if (this.visualViewportResizeRaf) {
        cancelAnimationFrame(this.visualViewportResizeRaf);
      }

      this.visualViewportResizeRaf = window.requestAnimationFrame(() => {
        window.scrollTo(currentScrollX, currentScrollY);
        this.visualViewportResizeRaf = null;
      });
    },
    handleScroll() {
      this.$store.commit('UPDATE_WINDOW_SCROLL_Y', window.scrollY);
      
      // Close dropdowns on scroll start (not continuously during scroll)
      // This prevents the visual disconnect between dropdown and trigger
      if (!this.isScrolling) {
        this.isScrolling = true;
        this.closeAllDropdowns();
      }
      
      // Reset scroll flag after scrolling stops
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.isScrolling = false;
      }, 150);
    },
    closeAllDropdowns() {
      // Check if any dropdowns are currently open
      const openDropdowns = document.querySelectorAll('.vs-dropdown--menu');
      const hasOpenDropdown = Array.from(openDropdowns).some(
        dropdown => getComputedStyle(dropdown).display !== 'none'
      );
      
      // Only trigger close if there's actually an open dropdown
      if (hasOpenDropdown) {
        // Use requestAnimationFrame to avoid interfering with scroll momentum
        requestAnimationFrame(() => {
          document.body.click();
        });
      }
    },
    handleDropdownItemClick(event) {
      // Global handler to auto-close dropdowns when clicking menu items
      // This only triggers on actual clicks, not scroll events, so it's iOS-safe
      
      const clickedElement = event.target;
      
      // Check if click is inside a dropdown menu
      const dropdownMenu = clickedElement.closest('.vs-dropdown--menu');
      if (!dropdownMenu) return;
      
      // Check if click is on a clickable menu item (li, a, button)
      const menuItem = clickedElement.closest('li, a, button, [role="menuitem"]');
      if (!menuItem) return;
      
      // Ignore clicks on dividers or disabled items
      if (menuItem.classList.contains('vs-divider') || menuItem.hasAttribute('disabled')) {
        return;
      }
      
      // Close the dropdown after a tiny delay to allow the click action to complete
      setTimeout(() => {
        document.body.click();
      }, 50);
    },
  },
};

</script>

<style>
/* Esto bloquea el scroll cuando hay un modal abierto */
body.modal-open {
  overflow: hidden;
}
</style>
